/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * La finestra per stampare il registro dei lavori dal calendario: le date
 * partono da quello che si sta guardando, e si possono cambiare. Tecnico e stati
 * nascosti sono quelli dei filtri del calendario, e la finestra lo dice: un PDF
 * con meno righe del previsto, senza spiegazione, sembra un errore.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Printer } from 'lucide-react';
import { apriRegistro } from '../utils/resoconto';
import { utentiAPI } from '../api/utenti';

const STATO_LABEL = {
  in_attesa: 'in attesa', confermato: 'confermati', in_corso: 'in corso',
  completato: 'completati', annullato: 'annullati',
};

export default function RegistroPdfModal({ dal, al, tecnico, nomeTecnico, nascosti, clienteIniziale = '', fonteFiltri = 'del calendario', onChiudi }) {
  const [d1, setD1] = useState(dal);
  const [d2, setD2] = useState(al);
  const [cliente, setCliente] = useState(clienteIniziale);
  const { data: clienti = [] } = useQuery({
    queryKey: ['utenti', 'cliente'],
    queryFn: () => utentiAPI.list('cliente'),
  });
  const [materiali, setMateriali] = useState(true);
  const [importi, setImporti] = useState(false);
  const [interno, setInterno] = useState(false);
  const [usaFiltri, setUsaFiltri] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState('');

  const haFiltri = Boolean(tecnico) || nascosti.length > 0;

  const genera = async () => {
    if (!d1 || !d2 || d2 < d1) { setErrore('Controlla le date: la fine non può venire prima dell\'inizio.'); return; }
    setErrore('');
    setInCorso(true);
    try {
      await apriRegistro({
        dal: d1, al: d2, cliente, materiali, importi: materiali && importi, interno,
        tecnico: usaFiltri ? tecnico : '', nascosti: usaFiltri ? nascosti : [],
      });
      onChiudi();
    } catch (e) {
      setErrore(e.message);
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onChiudi}>
      <div style={s.modale} onClick={(e) => e.stopPropagation()}>
        <div style={s.testa}>
          <h3 style={{ margin: 0, fontSize: '1.02rem' }}>Stampa il registro dei lavori</h3>
          <button style={s.icona} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        </div>
        <p style={s.spiega}>
          Giorno per giorno: data, orario, tecnico, intervento e la descrizione del lavoro svolto,
          con i totali delle ore. In PDF, pronto da stampare.
        </p>
        <div style={s.date}>
          <label style={s.label}>Dal<input type="date" style={s.input} value={d1} onChange={(e) => setD1(e.target.value)} /></label>
          <label style={s.label}>Al<input type="date" style={s.input} value={d2} onChange={(e) => setD2(e.target.value)} /></label>
        </div>
        <label style={{ ...s.label, marginBottom: '0.75rem' }}>
          Cliente
          <select style={s.input} value={cliente} onChange={(e) => setCliente(e.target.value)}>
            <option value="">Tutti i clienti</option>
            {clienti.map((c) => (
              <option key={c.id} value={c.id}>{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || '(cliente senza nome)'}</option>
            ))}
          </select>
        </label>

        <label style={s.voce}><input type="checkbox" checked={materiali} onChange={(e) => setMateriali(e.target.checked)} /> Aggiungi i materiali utilizzati</label>
        <label style={{ ...s.voce, paddingLeft: '1.5rem', opacity: materiali ? 1 : 0.45 }}>
          <input type="checkbox" checked={importi} disabled={!materiali} onChange={(e) => setImporti(e.target.checked)} /> con i prezzi
        </label>
        <label style={s.voce}><input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)} /> Versione interna (nomi degli artigiani esterni)</label>
        {haFiltri && (
          <label style={s.voce}>
            <input type="checkbox" checked={usaFiltri} onChange={(e) => setUsaFiltri(e.target.checked)} />
            <span>
              Usa i filtri {fonteFiltri}
              <span style={s.filtri}>
                {[tecnico ? `solo ${nomeTecnico}` : null,
                  nascosti.length ? `senza gli interventi ${nascosti.map((x) => STATO_LABEL[x] || x).join(', ')}` : null]
                  .filter(Boolean).join(' · ')}
              </span>
            </span>
          </label>
        )}

        {errore && <div style={s.errore}>{errore}</div>}
        <div style={s.azioni}>
          <button style={s.btnSecondario} onClick={onChiudi}>Annulla</button>
          <button style={s.btnPrimario} onClick={genera} disabled={inCorso}>
            <Printer size={16} /> {inCorso ? 'Preparazione...' : 'Apri il PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1100 },
  modale: { backgroundColor: 'white', borderRadius: '12px', padding: '1.2rem', width: '100%', maxWidth: '460px' },
  testa: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  icona: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'inline-flex', padding: '0.2rem' },
  spiega: { fontSize: '0.84rem', color: '#6B7280', lineHeight: 1.5, margin: '0.5rem 0 0.9rem' },
  date: { display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  label: { flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.65rem', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.92rem', fontFamily: 'inherit' },
  voce: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.88rem', padding: '0.3rem 0', cursor: 'pointer', lineHeight: 1.4 },
  filtri: { display: 'block', fontSize: '0.78rem', color: '#6B7280' },
  errore: { marginTop: '0.6rem', padding: '0.5rem 0.7rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.84rem' },
  azioni: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnSecondario: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
};
